import { useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { TranslationService } from '../../services/translation.service';
import { ConfigurationService } from '../../services/configuration.service';
import SearchAutocomplete, { type ISearchAutocompleteState } from '../Shared/SearchAutocomplete';
import type { IFindAutocompleteItem } from '../Shared/SearchAutocomplete/models/find-autocomplete-item.model';
import './style.scss';
import { AnalyticsService } from '../../services/analytics-service';
import { t } from '../../services/texts.service';

function SiteSearchMobile() {
  // === STATE ===
  const [autocompleteState, setAutompleteState] = useState<ISearchAutocompleteState>({ value: { searchText: '', selectedItem: undefined, preventSearch: false } });
  const [fullScreen, setFullScreen] = useState(false);

  // === REFS ===
  const isInitialMount = useRef(true);

  // === VARIABLES ===
  const searchPageUrl = ConfigurationService.get('sitesearch.searchpageurl');

  // === EFFECTS ===
  useEffect(() => {
    if (isInitialMount.current) {
      //NV-7327
      isInitialMount.current = false;
      return;
    }
    AnalyticsService.addEvent(AnalyticsService.MobileMenu, 'Öppna/stäng sökfunktion', fullScreen ? 'Öppna' : 'Stäng');
  }, [fullScreen]);

  // === EVENT HANDLERS ===
  function handleOnCloseButtonClick() {
    setAutompleteState({ value: { searchText: '', selectedItem: undefined, preventSearch: false } });
    setFullScreen(false);

    const mobileNavigationElement = document.getElementById('menu-mobile');
    mobileNavigationElement?.classList.remove('menu-mobile--top-0');
  }

  function handleOnSelect(item: IFindAutocompleteItem | undefined) {
    if (!item) return;
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('q', item.query);
    window.location.href = `${searchPageUrl}?${searchParams.toString()}`;
  }

  function handleOnSubmit(e: React.FormEvent<HTMLFormElement>) {
    if (!autocompleteState.value.searchText) {
      e.preventDefault();
    }
  }

  function handleOnSearchTextChange(newSearchText: string) {
    if (newSearchText) {
      setFullScreen(true);

      const mobileNavigationElement = document.getElementById('menu-mobile');
      mobileNavigationElement?.classList.add('menu-mobile--top-0');
    }
    setAutompleteState({ value: { searchText: newSearchText, selectedItem: undefined, preventSearch: false } });
  }

  return (
    <div id="site-search-mobile" className={`site-search-mobile d-print-none ${fullScreen ? 'site-search-mobile--full-screen' : ''}`}>
      <form action={searchPageUrl} method="get" role="search" aria-label={TranslationService.get('common.searchforms.labels.website')} className="site-search__form" onSubmit={handleOnSubmit}>
        <div className={`position-relative container-fluid py-2 align-items-center d-flex ${fullScreen ? 'site-search__container--autocomplete-opened' : ''}`}>
          <div className="d-flex flex-grow-1">
            <SearchAutocomplete
              id="header-search-mobile"
              state={autocompleteState}
              className="site-search-header-autocomplete search-autocomplete__input--with-clear-btn"
              placeholderText={TranslationService.get('common.search')}
              labelText={TranslationService.get('common.search')}
              positionRelative={false}
              inputName="q"
              onSelect={handleOnSelect}
              onSearchTextChange={handleOnSearchTextChange}
            />
            {autocompleteState.value.searchText && (
              <div className="position-relative">
                <button
                  type="button"
                  className="btn site-search-mobile__clear me-2 p-2 position-absolute"
                  aria-label={TranslationService.get('common.clear')}
                  aria-controls="header-search-mobile"
                  onClick={() => {
                    setAutompleteState({ value: { searchText: '', selectedItem: undefined, preventSearch: false } });
                  }}
                />
              </div>
            )}
          </div>
          <div className="align-self-stretch d-none d-sm-block ms-2">
            <button type="submit" className="btn btn-primary py-1 h-100" aria-label={t('Sök på vasttrafik', 'Search on vasttrafik')}>
              {TranslationService.get('common.search')}
            </button>
          </div>
          <div className={`align-self-center ${fullScreen ? 'ms-2' : ''}`}>
            <button
              type="button"
              className={`btn btn-tertiary ${fullScreen ? '' : 'd-none'}`}
              aria-label={t('Stäng sök', 'Close search')}
              aria-expanded="false"
              aria-controls={fullScreen ? 'site-search' : undefined}
              onClick={handleOnCloseButtonClick}
            >
              {t('Stäng', 'Close')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default SiteSearchMobile;
